import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Crosshair, MapPin, Gauge, Navigation, MountainSnow } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import DeviceCommands from './DeviceCommands'
import { equipmentOf, statusOf } from '../equipment'
import { loadGoogleMaps, geofenceColor, makeOrthoLayer } from '../lib/maps'
import { MarkerAnimator } from '../lib/anim'
import { clockSkewMs, serverNow } from '../lib/clock'
import { statusText } from '../i18n/status'

// Cor estável por equipamento (rastro de rota) — paleta "utility" da Volis.
const PALETTE = ['#15b79e', '#06aed4', '#6172f3', '#875bf7', '#d444f1', '#ef6820', '#f63d68', '#66c61c', '#eaaa08', '#2e90fa']
function colorForDevice(name) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length]
}

// Cor do marcador pelo estado operacional (mesmo color code da lista de equipamentos).
const STATUS_HEX = {
  op:      { fill: '#17b26a', stroke: '#0a7a47' }, // verde — em operação
  // Paragem aberta, uma cor por balde GMG (as mesmas do modelo de tempo).
  stop_delay:     { fill: '#1570ef', stroke: '#0b4a9c' }, // azul — atraso
  stop_nonprod:   { fill: '#dd2590', stroke: '#9b1663' }, // magenta — não produtivo
  stop_standby:   { fill: '#f79009', stroke: '#b54708' }, // âmbar — espera
  stop_breakdown: { fill: '#b42318', stroke: '#7a1710' }, // vermelho escuro — avaria
  stop:    { fill: '#f79009', stroke: '#b54708' }, // laranja — parado (sem operador)
  notelem: { fill: '#8b5cf6', stroke: '#6d28d9' }, // roxo — sem telemetria / online
  down:    { fill: '#f04438', stroke: '#b42318' }, // vermelho — sem comunicação
}
function statusColors(device) {
  const st = statusOf(device)
  const hex = STATUS_HEX[st.key] || STATUS_HEX.down
  return { ...hex, key: st.key }
}

// Legenda do color code (canto superior esquerdo do mapa).
const STATUS_LEGEND = [
  { hex: '#17b26a', labelKey: 'common:device_status.operating' },
  { hex: '#1570ef', labelKey: 'common:device_status.stop_delay' },
  { hex: '#dd2590', labelKey: 'common:device_status.stop_nonprod' },
  { hex: '#b42318', labelKey: 'common:device_status.stop_breakdown' },
  { hex: '#f79009', labelKey: 'common:device_status.stopped' },
  { hex: '#8b5cf6', labelKey: 'common:device_status.no_telemetry' },
  { hex: '#f04438', labelKey: 'common:device_status.no_comms' },
]

// Tamanho do canvas do marcador (badge ao centro + espaço pra seta de direção ao redor).
// Precisa comportar o pior caso da seta: r(17) + folga(5) + comprimento máximo(28) + stroke,
// senão a ponta é desenhada fora do viewBox e sai cortada acima de ~8 km/h.
const MARKER_SIZE = 108

// Acima de DENSITY_LIMIT devices o mapa esconde os marcadores FORA do viewport
// (reaparecem ao dar zoom/pan na região), para não desenhar/piscar centenas de
// ícones de uma vez. Abaixo desse limite, mostra todos.
const DENSITY_LIMIT = 250

// Marcador = ícone do equipamento cadastrado num badge (anel colorido por status).
// Quando o equipamento se move, desenha uma seta simples saindo do ícone na direção do rumo.
function makeMarkerSvg(device, iconData) {
  const { fill, stroke } = statusColors(device)
  const speed   = device.lastSpeedKmh ?? 0
  const heading = device.lastHeadingDeg
  const moving  = speed > 1 && heading != null

  const S = MARKER_SIZE, C = S / 2, R = 17, gap = 5
  const len  = Math.min(28, 12 + speed * 0.5)
  const tipY = C - R - gap - len
  const headY = tipY + 9

  let icon = ''
  if (iconData && iconData.inner) {
    const box = 26
    const scale = box / Math.max(iconData.vbW, iconData.vbH)
    const iw = iconData.vbW * scale, ih = iconData.vbH * scale
    icon = `<g transform="translate(${C - iw / 2},${C - ih / 2}) scale(${scale})" fill="#ffffff">${iconData.inner}</g>`
  }

  const arrow = moving ? `
    <g transform="rotate(${heading},${C},${C})" fill="none" stroke-linecap="round" stroke-linejoin="round">
      <g stroke="#ffffff" stroke-width="5.5" opacity="0.85">
        <line x1="${C}" y1="${C - R - gap}" x2="${C}" y2="${tipY}"/>
        <polyline points="${C - 6},${headY} ${C},${tipY} ${C + 6},${headY}"/>
      </g>
      <g stroke="${fill}" stroke-width="3">
        <line x1="${C}" y1="${C - R - gap}" x2="${C}" y2="${tipY}"/>
        <polyline points="${C - 6},${headY} ${C},${tipY} ${C + 6},${headY}"/>
      </g>
    </g>` : ''

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}">
    ${arrow}
    <circle cx="${C}" cy="${C}" r="${R}" fill="#0b0f17" fill-opacity="0.82" stroke="${stroke}" stroke-width="1"/>
    <circle cx="${C}" cy="${C}" r="${R}" fill="none" stroke="${fill}" stroke-width="2.5"/>
    ${icon}
  </svg>`
}

function makeIcon(device, iconData) {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(makeMarkerSvg(device, iconData))}`,
    scaledSize: new window.google.maps.Size(MARKER_SIZE, MARKER_SIZE),
    anchor: new window.google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE / 2),
  }
}

function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371000
  const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180
  const dφ = (lat2 - lat1) * Math.PI / 180, dλ = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function drawTrailForDevice(name, historyDataRef, trailRef, map) {
  const pts = historyDataRef.current[name] || []
  if (trailRef.current[name]) { trailRef.current[name].setMap(null); trailRef.current[name] = null }
  if (pts.length < 2) return
  trailRef.current[name] = new window.google.maps.Polyline({
    path: pts, map, geodesic: true, clickable: false,
    strokeColor: colorForDevice(name), strokeOpacity: 0.9, strokeWeight: 3, zIndex: 1,
  })
}

const fmt = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '—')

// Acima deste número a lista lateral renderiza só o começo (sem virtualização, mil
// linhas por frame de telemetria travavam a coluna) e o resto vira contagem.
const LIST_LIMIT = 200

export default function MapView({ visible }) {
  const { t } = useTranslation()
  const { devices, categories, mines, maps } = useWS()
  const { mineScope, matchesScope } = useScope()
  const scoped = useMemo(() => devices.filter(matchesScope), [devices, matchesScope])
  const [hiddenCount, setHiddenCount] = useState(0)
  const mapRef      = useRef(null)
  const gmapRef     = useRef(null)
  const markersRef  = useRef({})
  const markerKeyRef = useRef({})   // name -> última "chave de ícone" desenhada (evita redraw/piscar)
  const deviceCountRef = useRef(0)  // contagem atual (lida no listener de viewport sem stale)
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.Map)
  const [selected, setSelected] = useState(null)
  const [followed, setFollowed] = useState(null)  // name do VMC com a câmera fixada
  const followedRef = useRef(null)
  const visibleRef  = useRef(visible)
  const animRef     = useRef(null)
  if (!animRef.current) animRef.current = new MarkerAnimator()

  const historyDataRef    = useRef({})
  const trailRef          = useRef({})
  const historyConfigRef  = useRef({ points: 60, minDistM: 10 })
  const prevPositionsRef  = useRef({})
  const seededDevicesRef  = useRef(new Set())
  const didInitialCenterRef = useRef(false)

  // Cache dos SVGs de equipamento (inlinados no marcador). iconTick força rebuild dos
  // marcadores quando um ícone novo termina de carregar.
  const iconCacheRef = useRef({})
  const [iconTick, setIconTick] = useState(0)
  const getIcon = (key) => {
    const cached = iconCacheRef.current[key]
    if (cached && cached !== 'loading') return cached
    if (!cached) {
      iconCacheRef.current[key] = 'loading'
      fetch(`/equipments/${key}.svg`).then(r => r.text()).then(txt => {
        const vb = (txt.match(/viewBox="([^"]+)"/)?.[1] || '0 0 24 24').split(/\s+/).map(Number)
        const inner = txt.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '').replaceAll('currentColor', '#ffffff')
        iconCacheRef.current[key] = { inner, vbW: vb[2] || 24, vbH: vb[3] || 24 }
        setIconTick(t => t + 1)
      }).catch(() => { iconCacheRef.current[key] = { inner: '', vbW: 24, vbH: 24 }; setIconTick(t => t + 1) })
    }
    return null
  }

  const geoOverlaysRef = useRef([])
  const orthoLayersRef = useRef([])   // ImageMapType dos aerolevantamentos (abaixo dos markers)
  const orthoSigRef = useRef('')

  deviceCountRef.current = scoped.length
  visibleRef.current = visible
  followedRef.current = followed

  // Instante do fix no relógio LOCAL: o vmc.timestamp (disciplinado por GPS) menos o
  // desvio local↔servidor. Sem timestamp do device, ou com um absurdo (>60 s do
  // relógio do servidor), cai para a hora de chegada no servidor.
  const localFixTime = (d) => {
    const skew = clockSkewMs()
    if (d.lastFixTs && Math.abs(d.lastFixTs - serverNow()) < 60000) return d.lastFixTs - skew
    const arrived = Date.parse(d.lastTelemetryAt)
    return Number.isFinite(arrived) ? arrived - skew : Date.now()
  }

  // Selecionar outro equipamento solta a câmera fixada, senão o rAF desfaz o pan.
  const selectName = (name) => {
    if (followedRef.current && followedRef.current !== name) setFollowed(null)
    setSelected(name)
  }

  // Mostra/esconde marcadores conforme o viewport quando há muitos devices.
  // Lê apenas refs, então pode ser anexado uma única vez ao listener "idle".
  const applyViewport = () => {
    const map = gmapRef.current
    if (!map) return
    const gating = deviceCountRef.current > DENSITY_LIMIT
    const bounds = gating ? map.getBounds() : null
    let hidden = 0
    Object.values(markersRef.current).forEach(m => {
      const vis = !gating || (bounds ? bounds.contains(m.getPosition()) : true)
      if (!vis) hidden++
      if (m.getVisible() !== vis) m.setVisible(vis)
    })
    // Quantos marcadores o gating escondeu: sem isto, 980 de 1000 sumiam do mapa sem
    // nada dizer que estavam apenas fora do enquadramento.
    setHiddenCount(hidden)
  }

  useEffect(() => {
    loadGoogleMaps().then(() => setMapsReady(true)).catch(() => {})
    fetch('/api/config').then(r => r.json()).then(cfg => {
      if (cfg.historyPoints)   historyConfigRef.current.points   = cfg.historyPoints
      if (cfg.historyMinDistM) historyConfigRef.current.minDistM = cfg.historyMinDistM
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!mapsReady || !mapRef.current || gmapRef.current) return
    gmapRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 14, center: { lat: -23.5505, lng: -46.6333 }, mapTypeId: 'hybrid',
      fullscreenControl: false, streetViewControl: false, mapTypeControl: false, backgroundColor: '#0b0f17',
    })
    // Reaplica a visibilidade por viewport ao terminar zoom/pan (revela os devices
    // da região ao aproximar, esconde os de fora quando a frota é grande).
    gmapRef.current.addListener('idle', applyViewport)
    // Arrastar o mapa solta a câmera fixada. Detecção por GESTO do operador
    // (pointerdown + movimento), não pelo 'dragstart' do gmaps: panTo/animações
    // programáticas também disparam dragstart e derrubavam o follow sozinhas.
    const el = mapRef.current
    let downAt = null
    const onDown = (e) => { downAt = [e.clientX, e.clientY] }
    const onMove = (e) => {
      if (!downAt) return
      if (Math.abs(e.clientX - downAt[0]) > 5 || Math.abs(e.clientY - downAt[1]) > 5) {
        downAt = null
        setFollowed(null)
      }
    }
    const onUp = () => { downAt = null }
    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', onUp)
  }, [mapsReady])

  // Loop único de animação: renderiza cada marcador visível um pouco "no passado"
  // (atraso adaptativo por device, dentro do MarkerAnimator) e mantém a câmera no
  // VMC fixado. O desenho do ícone (seta/cor) continua por conta da guarda do iconKey,
  // nunca daqui: redesenhar o data-URI por frame é o flicker que ela existe pra evitar.
  useEffect(() => {
    if (!mapsReady) return
    let raf
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const map = gmapRef.current
      if (!map || !visibleRef.current || document.hidden) return
      const now = Date.now()
      // Epsilon de ~0.25 px no zoom atual: abaixo disso o setPosition é ruído.
      const centerLat = map.getCenter()?.lat() ?? 0
      const mPerPx = 156543.03392 * Math.cos(centerLat * Math.PI / 180) / 2 ** (map.getZoom() ?? 14)
      const epsDeg = (mPerPx * 0.25) / 111320
      for (const [name, m] of Object.entries(markersRef.current)) {
        if (!m.getVisible()) continue
        const s = animRef.current.sample(name, now)
        if (!s) continue
        const cur = m.getPosition()
        if (!cur || Math.abs(cur.lat() - s.lat) > epsDeg || Math.abs(cur.lng() - s.lng) > epsDeg) {
          m.setPosition({ lat: s.lat, lng: s.lng })
        }
        if (followedRef.current === name) map.setCenter({ lat: s.lat, lng: s.lng })
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [mapsReady])

  // Solta marcadores, rastros, overlays e listeners na saída da tela.
  useEffect(() => () => {
    const gmaps = window.google?.maps
    Object.values(markersRef.current).forEach(m => { gmaps?.event.clearInstanceListeners(m); m.setMap(null) })
    markersRef.current = {}
    markerKeyRef.current = {}
    animRef.current.clear()
    Object.values(trailRef.current).forEach(tr => tr && tr.setMap(null))
    trailRef.current = {}
    geoOverlaysRef.current.forEach(o => o.setMap(null))
    geoOverlaysRef.current = []
    const map = gmapRef.current
    if (map) {
      orthoLayersRef.current.forEach(layer => {
        const idx = map.overlayMapTypes.getArray().indexOf(layer)
        if (idx >= 0) map.overlayMapTypes.removeAt(idx)
      })
      gmaps?.event.clearInstanceListeners(map)
    }
    orthoLayersRef.current = []
    orthoSigRef.current = ''
    gmapRef.current = null
  }, [])

  // Overlay das geofences (polígonos/círculos por tipo), refeito ao abrir a view.
  // Recortado pelo escopo de mina, como os marcadores: cercas de outra mina sobre a
  // frota da mina selecionada eram a mesma tela dizendo duas coisas.
  useEffect(() => {
    if (!mapsReady || !gmapRef.current || !visible) return
    const inScope = (f) => {
      if (mineScope === 'all') return true
      if (mineScope === 'none') return f.mine_id == null
      return Number(f.mine_id) === mineScope
    }
    fetch('/api/geofences').then(r => r.json()).then(list => {
      geoOverlaysRef.current.forEach(o => o.setMap(null))
      geoOverlaysRef.current = []
      ;(Array.isArray(list) ? list : []).filter(f => f.active && inScope(f)).forEach(f => {
        const color = geofenceColor(f.kind)
        let g = f.geometry || {}
        if (typeof g === 'string') { try { g = JSON.parse(g) } catch { g = {} } }
        const style = { strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2, fillColor: color, fillOpacity: 0.12, clickable: false, zIndex: 2 }
        let overlay = null
        if (f.shape === 'circle' && g.center) {
          overlay = new window.google.maps.Circle({ map: gmapRef.current, center: { lat: g.center.lat, lng: g.center.lng }, radius: g.radius_m || 0, ...style })
        } else if (Array.isArray(g.points) && g.points.length >= 3) {
          overlay = new window.google.maps.Polygon({ map: gmapRef.current, paths: g.points.map(p => ({ lat: p.lat, lng: p.lng })), ...style })
        }
        if (overlay) geoOverlaysRef.current.push(overlay)
      })
    }).catch(() => {})
  }, [mapsReady, visible, mineScope])

  // Overlay dos aerolevantamentos (raster): ImageMapType em overlayMapTypes, que fica
  // no pane overlayLayer — ABAIXO dos markers (não precisa zIndex). Só rebuilda quando o
  // conjunto de mapas ready+active muda (id+updated_at), evitando churn no tick da telemetria.
  useEffect(() => {
    if (!mapsReady || !gmapRef.current) return
    const inScope = (m) => mineScope === 'all'
      || (mineScope === 'none' ? m.mine_id == null : Number(m.mine_id) === mineScope)
    const active = (maps || []).filter(m => m.status === 'ready' && m.active && inScope(m))
    const sig = active.map(m => `${m.id}:${m.updated_at}`).join('|')
    if (sig === orthoSigRef.current) return
    orthoSigRef.current = sig
    const map = gmapRef.current
    orthoLayersRef.current.forEach(layer => {
      const idx = map.overlayMapTypes.getArray().indexOf(layer)
      if (idx >= 0) map.overlayMapTypes.removeAt(idx)
    })
    orthoLayersRef.current = active.map(m => {
      const layer = makeOrthoLayer(window.google.maps, m)
      map.overlayMapTypes.insertAt(0, layer)
      return layer
    })
  }, [mapsReady, maps, mineScope])

  useEffect(() => {
    if (!visible || !gmapRef.current) return
    setTimeout(() => {
      window.google.maps.event.trigger(gmapRef.current, 'resize')
      if (didInitialCenterRef.current || Object.keys(markersRef.current).length === 0) return
      const bounds = new window.google.maps.LatLngBounds()
      Object.values(markersRef.current).forEach(m => bounds.extend(m.getPosition()))
      gmapRef.current.fitBounds(bounds, 80)
      didInitialCenterRef.current = true
    }, 100)
  }, [visible])

  useEffect(() => {
    if (!gmapRef.current) return

    // Remove marcadores/rastros de devices fora do escopo de mina atual.
    const scopedNames = new Set(scoped.map(d => d.name))
    Object.keys(markersRef.current).forEach(name => {
      if (scopedNames.has(name)) return
      markersRef.current[name].setMap(null)
      delete markersRef.current[name]
      delete markerKeyRef.current[name]
      animRef.current.remove(name)
      if (name === followedRef.current) setFollowed(null)
      if (trailRef.current[name]) { trailRef.current[name].setMap(null); delete trailRef.current[name] }
    })

    const bounds = new window.google.maps.LatLngBounds()
    let hasBounds = false

    scoped.forEach(d => {
      if (d.lastLat == null || d.lastLon == null) return
      const pos = { lat: d.lastLat, lng: d.lastLon }
      bounds.extend(pos)
      hasBounds = true

      const iconData = getIcon(equipmentOf(d, categories, mines).svg)

      // Chave do ícone: só o que muda o desenho (status, velocidade, rumo, svg pronto).
      // Redesenhar 300+ ícones a cada update do WS é o que fazia o mapa "piscar".
      const sp = Math.round(d.lastSpeedKmh ?? 0)
      const moving = (d.lastSpeedKmh ?? 0) > 1 && d.lastHeadingDeg != null
      const iconKey = `${statusOf(d).key}|${sp}|${moving ? Math.round(d.lastHeadingDeg / 5) : 'x'}|${iconData ? 1 : 0}`

      const snap = { lat: pos.lat, lng: pos.lng, heading: d.lastHeadingDeg, speed: d.lastSpeedKmh ?? 0, t: localFixTime(d) }
      if (markersRef.current[d.name]) {
        const m = markersRef.current[d.name]
        // Quem move marcador visível é o loop de rAF (interpolando). Marcador escondido
        // pelo gating de viewport recebe a posição crua: o applyViewport decide a
        // visibilidade por getPosition(), e um escondido parado nunca seria revelado.
        animRef.current.push(d.name, snap)
        if (!m.getVisible()) m.setPosition(pos)
        if (markerKeyRef.current[d.name] !== iconKey) {
          m.setIcon(makeIcon(d, iconData))
          markerKeyRef.current[d.name] = iconKey
        }
      } else {
        const marker = new window.google.maps.Marker({ position: pos, map: gmapRef.current, icon: makeIcon(d, iconData), title: d.name, zIndex: 10 })
        marker.addListener('click', () => selectName(d.name))
        markersRef.current[d.name] = marker
        markerKeyRef.current[d.name] = iconKey
        animRef.current.seed(d.name, snap)
        // Redesenha o rastro guardado quando o device volta ao escopo.
        if (!trailRef.current[d.name] && (historyDataRef.current[d.name] || []).length >= 2) {
          drawTrailForDevice(d.name, historyDataRef, trailRef, gmapRef.current)
        }
      }

      if (!seededDevicesRef.current.has(d.name)) {
        seededDevicesRef.current.add(d.name)
        prevPositionsRef.current[d.name] = pos
        fetch(`/api/devices/${encodeURIComponent(d.name)}/history`).then(r => r.json()).then(pts => {
          historyDataRef.current[d.name] = Array.isArray(pts) ? pts : []
          drawTrailForDevice(d.name, historyDataRef, trailRef, gmapRef.current)
        }).catch(() => {})
        return
      }

      const prev = prevPositionsRef.current[d.name]
      if (!prev) { prevPositionsRef.current[d.name] = pos; return }
      if (haversineM(prev.lat, prev.lng, pos.lat, pos.lng) < historyConfigRef.current.minDistM) return

      prevPositionsRef.current[d.name] = pos
      const hist = historyDataRef.current[d.name] || []
      hist.unshift(pos)
      if (hist.length > historyConfigRef.current.points) hist.length = historyConfigRef.current.points
      historyDataRef.current[d.name] = hist
      drawTrailForDevice(d.name, historyDataRef, trailRef, gmapRef.current)
    })

    if (visible && !didInitialCenterRef.current && hasBounds) {
      if (Object.keys(markersRef.current).length === 1) gmapRef.current.setCenter(bounds.getCenter())
      else gmapRef.current.fitBounds(bounds, 80)
      didInitialCenterRef.current = true
    }

    applyViewport() // aplica o gating de viewport após (re)criar/atualizar marcadores
  }, [scoped, visible, categories, mines, iconTick])

  const centerOn = (d) => {
    const map = gmapRef.current
    if (!map || d?.lastLat == null || d?.lastLon == null) return
    map.panTo({ lat: d.lastLat, lng: d.lastLon })
    if (map.getZoom() < 15) map.setZoom(16)
  }

  const selectDevice = (d) => {
    selectName(d.name)
    centerOn(d)
  }

  const focusDevice = (d) => {
    if (d?.lastLat == null || d?.lastLon == null) return
    gmapRef.current?.panTo({ lat: d.lastLat, lng: d.lastLon })
    gmapRef.current?.setZoom(16)
    selectName(d.name)
  }

  const toggleFollow = (d) => {
    if (followed === d.name) { setFollowed(null); return }
    focusDevice(d)
    setFollowed(d.name)
  }

  const sel = selected ? scoped.find(d => d.name === selected) || null : null

  return (
    <div className="h-full relative">
      {!mapsReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
          <div className="text-center text-slate-400">
            <div className="text-4xl mb-2">🗺</div>
            <p className="text-sm">{t('ops:map.loading_maps')}</p>
            <p className="text-xs mt-1 text-slate-500">{t('ops:map.check_api_key')} <code>.env</code></p>
          </div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />

      {/* Legenda do color code de status (canto superior esquerdo, sem cobrir o mapa) */}
      {mapsReady && (
        <div className="absolute top-3 left-3 bg-white/85 dark:bg-slate-900/85 backdrop-blur rounded-lg shadow-md border border-black/5 dark:border-white/10 px-3 py-2 text-[11px] text-slate-700 dark:text-slate-200 pointer-events-none">
          <p className="font-semibold text-brand-600 dark:text-brand-300 mb-1.5">{t('ops:map.status_title')}</p>
          <ul className="space-y-1">
            {STATUS_LEGEND.map(s => (
              <li key={s.labelKey} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0 ring-1 ring-black/10" style={{ backgroundColor: s.hex }} />
                {t(s.labelKey)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coluna direita flutuante: lista + painel do equipamento selecionado (sem escurecer o mapa) */}
      <div className="absolute top-3 right-3 bottom-3 w-72 flex flex-col gap-3 pointer-events-none">
        <div className="pointer-events-auto bg-slate-900/85 backdrop-blur rounded-xl shadow-lg border border-white/10 overflow-hidden text-slate-200 flex flex-col max-h-[45%]">
          <div className="px-3 py-2.5 border-b border-white/10 text-xs font-semibold text-slate-300 shrink-0">
            {t('ops:map.equipment_count', { count: scoped.length })}
            {hiddenCount > 0 && (
              <span className="ml-1 font-normal text-status-stop" title={t('ops:map.hidden_hint')}>
                · {t('ops:map.hidden_outside', { n: hiddenCount })}
              </span>
            )}
          </div>
          <div className="overflow-y-auto scrollbar-thin">
            {scoped.slice(0, LIST_LIMIT).map(d => (
              <button key={d.name} onClick={() => selectDevice(d)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-white/5 last:border-0 ${selected === d.name ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/30" style={{ backgroundColor: statusColors(d).fill }} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-100 truncate">{equipmentOf(d, categories, mines).name || d.name}</p>
                  {d.lastSpeedKmh != null && <p className="text-[11px] text-slate-400">{d.lastSpeedKmh.toFixed(1)} km/h</p>}
                </div>
                <span className="w-3 h-0.5 rounded-full shrink-0" style={{ backgroundColor: colorForDevice(d.name) }} title={t('ops:map.trail_color')} />
              </button>
            ))}
            {scoped.length > LIST_LIMIT && (
              <p className="px-3 py-2 text-[11px] text-slate-400 border-t border-white/5">
                {t('ops:map.list_truncated', { shown: LIST_LIMIT, total: scoped.length })}
              </p>
            )}
            {scoped.length === 0 && <p className="px-3 py-4 text-xs text-slate-400">{t('ops:map.none_with_position')}</p>}
          </div>
        </div>

        {sel && (
          <div className="pointer-events-auto bg-slate-900/90 backdrop-blur rounded-xl shadow-xl border border-white/10 text-slate-200 overflow-hidden flex flex-col">
            <div className="px-3 py-2.5 border-b border-white/10 flex items-start justify-between gap-2 shrink-0">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{equipmentOf(sel, categories, mines).name || sel.name}</p>
                <p className="text-[11px] text-slate-400 truncate flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColors(sel).fill }} />
                  {statusText(t, statusOf(sel))} · {equipmentOf(sel, categories, mines).label}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-200 shrink-0"><X size={16} /></button>
            </div>

            <div className="overflow-y-auto scrollbar-thin">
              <div className="px-3 py-3 grid grid-cols-2 gap-2 text-[11px] text-slate-300">
                <span className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-500" /><span className="tabular-nums">{fmt(sel.lastLat, 4)}, {fmt(sel.lastLon, 4)}</span></span>
                <span className="flex items-center gap-1.5"><MountainSnow size={12} className="text-slate-500" /><span className="tabular-nums">{fmt(sel.lastAltM)} m</span></span>
                <span className="flex items-center gap-1.5"><Gauge size={12} className="text-slate-500" /><span className="tabular-nums">{fmt(sel.lastSpeedKmh)} km/h</span></span>
                <span className="flex items-center gap-1.5"><Navigation size={12} className="text-slate-500" /><span className="tabular-nums">{fmt(sel.lastHeadingDeg)}°</span></span>
              </div>
              <div className="px-3 pb-3">
                <DeviceCommands deviceName={sel.name} theme="dark" canUpdate={!!sel.capabilities?.update} />
              </div>
            </div>

            <div className="px-3 py-2 border-t border-white/10 shrink-0">
              <button onClick={() => toggleFollow(sel)}
                className={`w-full inline-flex items-center justify-center gap-1 text-xs ${followed === sel.name ? 'text-brand-300 hover:text-brand-200' : 'text-slate-300 hover:text-white'}`}>
                <Crosshair size={13} /> {followed === sel.name ? t('ops:map.unfollow_vmc') : t('ops:map.follow_vmc')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
